import styled from "styled-components";
import ErrorMessage from "../../shared/ErrorMessage";
import Button from "../../shared/Button";
import AutocompleteField from "./AutocompleteField";
import DatesRangeField from "./HotelDateField";
import { useTranslation } from "../../../i18n";
import { HotelsSearchFormProps } from "../../../types/HotelsSearchFormProps";
import { Breakpoint } from "../../../enums/Breakpoint";
import ExternalLayoutPerks from "../../shared/ExternalLayout/ExternalLayoutPerks";
import { LayoutType } from "../../../enums/LayoutType";
import { InputKey, useWizardContext } from "../../../contexts/hotels/WizardContext";
import RoomsCountField from "../../SharedSearchFormFields/Hotels/ExternalLayout/RoomsCountField/RoomsCountField";
import SearchForPackagesCheckbox from "../../SharedSearchFormFields/Hotels/ExternalLayout/SearchForPackagesCheckbox";
import { SearchFormTabValues } from "../../../enums/SearchFormTabValues";

export const HotelsSearchForm = ({
  errors,
  hasErrors,
  touched,
  onChange,
  onSubmit,
  values,
  isSwitchToPackageChecked,
  onIsSwitchToPackageCheckedChange,
}: HotelsSearchFormProps) => {
  const { t } = useTranslation();
  const { isWizard, mobileModalAnimations, setInputRef, stopWizard } = useWizardContext();

  return (
    <ExternalLayoutFormContainer>
      {!!onIsSwitchToPackageCheckedChange && (
        <StyledSearchForPackagesCheckbox
          isChecked={!!isSwitchToPackageChecked}
          onChange={onIsSwitchToPackageCheckedChange}
        />
      )}
      <SearchForm onSubmit={onSubmit}>
        <FieldsContainer>
          <AutocompleteField
            errors={errors}
            onChange={onChange}
            touched={touched}
            values={values}
          />
          <DatesRangeField errors={errors} onChange={onChange} touched={touched} values={values} />
          <RoomsCountField
            onChange={onChange}
            onSubmit={onSubmit}
            values={values.rooms}
            isWizard={isWizard}
            mobileModalAnimations={mobileModalAnimations}
            setInputRef={setInputRef(InputKey.RoomCount)}
            stopWizard={stopWizard}
          />
          <SubmitButton type="submit" onClick={onSubmit} variant="rectangle">
            {t("tix_search_form_search_hotels")}
          </SubmitButton>
        </FieldsContainer>
        {hasErrors && (
          <ErrorsRow>
            <FormErrorMessage>
              <ErrorMessage
                error="tix_search_form_err_hotels_search_form_global"
                layout={LayoutType.External}
              />
            </FormErrorMessage>
          </ErrorsRow>
        )}
      </SearchForm>
      <ExternalLayoutPerks productType={SearchFormTabValues.Hotels} />
    </ExternalLayoutFormContainer>
  );
};

const ExternalLayoutFormContainer = styled.div`
  padding: 20px 0 16px 0;
  background: ${({ theme }) => theme.darkBackground};
  display: flex;
  flex-direction: column;
  gap: 20px;

  @media ${Breakpoint.MinTablet} {
    border-radius: ${({ theme }) => theme.ovalBorderRadius};
    padding: 10px 0 12px 0;
    gap: 10px;
  }

  @media ${Breakpoint.MaxMobile} {
    ${({ theme }) =>
      !!theme.form.container?.mobileBackgroundColor &&
      `background: ${theme.form.container?.mobileBackgroundColor};`}
  }
`;

const SearchForm = styled.form`
  padding: 0px 20px;

  @media ${Breakpoint.MinTablet} {
    padding: 0px 10px;
  }
`;

const FieldsContainer = styled.div`
  display: flex;
  gap: 10px;
  flex-wrap: wrap;

  @media ${({ theme }) => theme.breakpoints.maxMobile} {
    gap: 5px;
  }

  > * {
    border-radius: ${({ theme }) => theme.ovalBorderRadius};
    flex: 1;
    min-width: calc((100% - 30px) / 4);

    @media ${({ theme }) => theme.breakpoints.maxTablet} {
      flex-basis: 49%;
      min-width: calc((100% - 10px) / 2);
    }

    @media ${({ theme }) => theme.breakpoints.maxMobile} {
      flex-basis: 100%;
    }
  }
`;

const FormErrorMessage = styled.div`
  display: inline-flex;
`;

const ErrorsRow = styled.div`
  margin: 10px;
  align-items: flex-start;
  display: flex;
  flex-direction: row-reverse;
  justify-content: flex-end;
`;

const SubmitButton = styled(Button)`
  font-size: 23px;
  font-family: ${({ theme }) => theme.heavyFont};
  display: flex;
  margin: 0 0 0 auto;
  width: 100%;
  height: 80px;
  border-radius: ${({ theme }) => theme.ovalBorderRadius};
`;

const StyledSearchForPackagesCheckbox = styled(SearchForPackagesCheckbox)`
  padding: 0px 20px;

  @media ${Breakpoint.MinTablet} {
    padding: 0px 10px;
  }
`;

export default HotelsSearchForm;
